import difference from 'lodash/difference';
import { createSelector } from '@atlassian/jira-common-selectors/src/index.tsx';
import { isSubtaskHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import type { Card, CardEntryGroup } from '../../../model/types.tsx';
import type { CardEntityState } from '../../reducers/entities/card/types.tsx';
import type { SelectedState } from '../../reducers/ui/cards/selected/types.tsx';
import { makeGetCardGroupByChildId } from '../card-entries/index.tsx';
import { getCards, getSelectedCards } from '../cards/index.tsx';

export const makeIsAnySelectedOrphanedSubtasks = createSelector(
	[getSelectedCards, getCards],
	(selectedCards: SelectedState, cards: CardEntityState): boolean =>
		selectedCards.some((cardId): boolean => {
			const card = cards[cardId];
			const isSubtask: boolean = !!card && isSubtaskHierarchyLevel(card.typeHierarchyLevel);

			const isOrphaned: boolean = !!card?.parentId && !selectedCards.includes(card.parentId);
			return isSubtask && isOrphaned;
		}),
);

export const makeIsAllSelectedSiblingSubtasks = createSelector(
	[getSelectedCards, getCards, makeGetCardGroupByChildId],
	(
		selectedCards: SelectedState,
		cards: CardEntityState,
		getCardGroupByChildId: (cardId: number) => CardEntryGroup | undefined,
	): boolean =>
		!!selectedCards.length &&
		selectedCards.every((cardId): boolean => {
			const card: Card = cards[cardId];
			const isSubtask: boolean = !!card && isSubtaskHierarchyLevel(card.typeHierarchyLevel);
			if (isSubtask) {
				const cardGroup: CardEntryGroup | undefined = getCardGroupByChildId(cardId);

				return !!cardGroup && difference(selectedCards, cardGroup.cardIds).length === 0;
			}

			return false;
		}),
);
